// Host side of the Atomic Mode ↔ atomify iframe status channel.
//
// Our vendored atomify build posts lifecycle messages to the parent window
// while it loads and runs an embedded simulation (see
// vendor/atomify/src/components/EmbedStatusReporter.tsx). This module is the
// typed receiver: it validates those messages and exposes a small subscribe
// helper so the Atomic Mode overlay can show a loading state, a live log, and
// surface errors instead of a silent blank iframe.
//
// This is a one-way *status* channel out of the GPL bundle — we only read
// what atomify tells us; we never call into it. The license boundary holds.

export const ATOMIFY_EMBED_SOURCE = "atomify-embed";
export const ATOMIFY_HOST_SOURCE = "atomify-host";

/** A simulation pushed host → iframe via postMessage (no URL size limit). */
export interface LoadSimulationPayload {
  id: string;
  inputScript: string;
  /** fileName -> file text content. Must include `inputScript`. */
  files: Record<string, string>;
  /** LAMMPS potential file names the iframe fetches from its own bundle. */
  potentialFiles?: string[];
  autostart?: boolean;
}

/** The full message the host posts into the iframe to load a simulation. */
export interface LoadSimulationMessage {
  source: typeof ATOMIFY_HOST_SOURCE;
  type: "load-simulation";
  simulation: LoadSimulationPayload;
}

/** Build the load-simulation message for `iframe.contentWindow.postMessage`. */
export function buildLoadSimulationMessage(
  simulation: LoadSimulationPayload,
): LoadSimulationMessage {
  return { source: ATOMIFY_HOST_SOURCE, type: "load-simulation", simulation };
}

export type AtomifyEmbedEvent =
  | { type: "ready" }
  | { type: "wasm-ready" }
  | { type: "loaded"; simulationId: string; files: string[] }
  | { type: "running" }
  | { type: "stopped" }
  | { type: "error"; message: string }
  | { type: "log"; message: string }
  | {
      // A live sample from the running LAMMPS simulation.
      type: "measurement";
      step: number;
      temp: number | null;
      pe: number | null;
      ke: number | null;
      msd: number | null;
      numAtoms: number;
      numBonds: number;
    }
  | {
      // Raw LAMMPS console output (thermo table, run setup, warnings).
      type: "raw-log";
      lines: string[];
    };

/** Narrow an arbitrary postMessage payload to an AtomifyEmbedEvent. */
export function parseEmbedEvent(data: unknown): AtomifyEmbedEvent | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.source !== ATOMIFY_EMBED_SOURCE) return null;
  switch (d.type) {
    case "ready":
    case "wasm-ready":
    case "running":
    case "stopped":
      return { type: d.type };
    case "loaded":
      return {
        type: "loaded",
        simulationId: String(d.simulationId ?? ""),
        files: Array.isArray(d.files) ? d.files.map(String) : [],
      };
    case "error":
      return { type: "error", message: String(d.message ?? "Unknown error") };
    case "log":
      return { type: "log", message: String(d.message ?? "") };
    case "measurement": {
      const num = (v: unknown): number | null =>
        typeof v === "number" && Number.isFinite(v) ? v : null;
      return {
        type: "measurement",
        step: num(d.step) ?? 0,
        temp: num(d.temp),
        pe: num(d.pe),
        ke: num(d.ke),
        msd: num(d.msd),
        numAtoms: num(d.numAtoms) ?? 0,
        numBonds: num(d.numBonds) ?? 0,
      };
    }
    case "raw-log":
      return {
        type: "raw-log",
        lines: Array.isArray(d.lines) ? d.lines.map(String) : [],
      };
    default:
      return null;
  }
}

/**
 * Subscribe to atomify embed status events. Returns an unsubscribe function.
 * Optionally filter to messages from a specific iframe's content window.
 */
export function subscribeEmbedEvents(
  handler: (event: AtomifyEmbedEvent) => void,
  sourceWindow?: Window | null,
): () => void {
  const listener = (e: MessageEvent) => {
    if (sourceWindow && e.source !== sourceWindow) return;
    const event = parseEmbedEvent(e.data);
    if (event) handler(event);
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}

/** Human-readable one-liner for a status event, for the log panel. */
export function describeEmbedEvent(event: AtomifyEmbedEvent): string {
  switch (event.type) {
    case "ready":
      return "Atomify app loaded.";
    case "wasm-ready":
      return "LAMMPS engine (WebAssembly) ready.";
    case "loaded":
      return `Simulation accepted: ${event.simulationId} (${event.files.join(", ")}).`;
    case "running":
      return "Molecular dynamics running…";
    case "stopped":
      return "Simulation stopped.";
    case "error":
      return `Error: ${event.message}`;
    case "log":
      return event.message;
    case "measurement":
      // Rendered as the live neon readout, not a plain log line.
      return `step ${event.step}`;
    case "raw-log":
      // Rendered verbatim as raw engine lines, not a one-liner.
      return event.lines.join("\n");
  }
}
